import { useRef } from 'react'
import { useUpdatePitchMutation } from './api'
import { useImageUpload } from './use-image-upload'

/**
 * Hook that manages pitch cover image upload and removal.
 * Returns a file input ref, trigger function, change handler, and remove handler.
 */
export function usePitchCoverImage(pitchId: string) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { upload: uploadImage, isUploading } = useImageUpload()
  const updatePitch = useUpdatePitchMutation(pitchId)

  const triggerUpload = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const imageUrl = await uploadImage(file)
      await updatePitch.mutateAsync({
        path: { pitchId },
        body: { videoCoverImageUrl: imageUrl },
      })
    } catch {
      // Error handled by useImageUpload
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeCoverImage = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    updatePitch.mutate({
      path: { pitchId },
      body: { videoCoverImageUrl: null },
    })
  }

  return {
    fileInputRef,
    triggerUpload,
    handleFileChange,
    removeCoverImage,
    isUploading,
    isUpdating: updatePitch.isPending,
  }
}
